import { SUBSCRIPTION_TERMS_VERSION } from '@geo-admin/shared/contract/api/billing.ts';
import { BRAND, COMPANY } from '../../lib/company.ts';
import type { LegalDoc } from '../types.ts';

/**
 * [서비스 명세] — 약관 표와 구독 제안 화면(`ServiceSpec`)이 함께 읽는다. 한 곳이라 두 화면이 어긋나지 않는다.
 * `detail`·`quantity` 는 약관 본문이다(고치면 `SUBSCRIPTION_TERMS_VERSION` 을 올린다). `brief`·`short`·`order`(화면 순서) 는 화면용이다 — 약관 표는 배열 순서 그대로다
 */
export const SUBSCRIPTION_SERVICES: {
  name: string;
  detail: string;
  quantity: string;
  brief: string;
  short: string;
  order: number;
}[] = [
  {
    name: 'GEO 홈페이지 구축·관리',
    detail:
      '기존 홈페이지를 분석해 AI 추천에 맞게 새로 설계·제작하고, 공개 전 품질을 점검하며, 공개 후 업데이트와 유지보수를 합니다',
    quantity: '구축 1회 + 상시 관리',
    brief: 'AI 추천에 맞춘 홈페이지 제작과 유지보수',
    short: '구축 + 상시 관리',
    order: 1,
  },
  {
    name: 'AI 인용 사이트 관리',
    detail: 'AI가 참고하는 업종 순위·추천 사이트에 대상 브랜드를 등재하고 상위 노출을 관리합니다',
    quantity: '상시',
    brief: '업종 순위·추천 사이트 등재와 상위 노출',
    short: '상시',
    order: 5,
  },
  {
    name: '뉴스 기사 발간',
    detail: '대상 브랜드의 진료·서비스·활동 소식을 기획부터 작성·발행까지 대행합니다',
    quantity: '구독 기간마다 1건',
    brief: '브랜드 소식 기획·작성·발행 대행',
    short: '월 1건',
    order: 2,
  },
  {
    name: '블로그 발행',
    detail: '그달의 기사와 연계한 블로그, 목표 질문과 연계한 AI 최적화 블로그를 발행합니다',
    quantity: '구독 기간마다 30건',
    brief: 'AI 최적화 블로그',
    short: '월 30건',
    order: 3,
  },
  {
    name: '숏폼 영상 제작·배포',
    detail: '영상을 기획·제작해 유튜브·인스타그램·X·스레드·틱톡에 회사의 계정으로 배포합니다',
    quantity: '구독 기간마다 2편(5개 채널)',
    brief: '유튜브·인스타그램·X·스레드·틱톡 배포',
    short: '월 2편',
    order: 4,
  },
];

/**
 * 구독 서비스 이용약관 — 구독을 시작하기 전에 동의받는다. **서면 계약서 작성을 갈음한다**(운영자 결정 2026-10-12).
 * 서비스 이용 계약서(web/src/contract/template.ts)에서 필요한 조항만 가져와 약관으로 다시 썼다 — 당사자 정보·서명란은 없다.
 * 이용자는 구독을 시작한 계정 — 그 브랜드의 소속자(소유자·관리자)다 (261013-account-brand-membership).
 *
 * ★ 문안을 고치면 `SUBSCRIPTION_TERMS_VERSION` 을 올린다 — 구독마다 어느 판에 동의했는지가 남는다(`billing_subscription.terms_version`).
 *   서버는 현재 판이 아닌 동의를 거부한다(옛 화면에서 동의하는 일이 없게).
 * ★ 시작 화면의 요약(`SUBSCRIPTION_TERMS_SUMMARY`)은 이 본문과 **같은 내용**이어야 한다 — 한쪽만 고치지 않는다.
 * ★ 최소 이용기간·위약금은 두지 않는다 — 약관에서도 뺐다(운영자 결정 2026-10-13, 261011-subscription-billing-payup).
 */
export const subscriptionTerms: LegalDoc = {
  title: `${BRAND.name} 구독 서비스 이용약관`,
  effectiveDate: SUBSCRIPTION_TERMS_VERSION,
  blocks: [
    { kind: 'h2', text: '제1조 (목적)' },
    {
      kind: 'p',
      text: `이 약관은 ${COMPANY.name}(이하 "회사")가 제공하는 ${BRAND.name} AI 검색 최적화(GEO) 월 구독 서비스(이하 "서비스")의 이용 조건과 절차, 회사와 이용자의 권리·의무를 정합니다. 서비스는 ChatGPT 등 AI 검색의 인용을 위해 콘텐츠를 발행하고 관리하는 서비스입니다.`,
    },

    { kind: 'h2', text: '제2조 (정의)' },
    {
      kind: 'ol',
      items: [
        '"이용자"란 대상 브랜드를 관리하는 계정으로서 이 약관에 동의하고 구독을 시작한 자를 말합니다.',
        '"대상 브랜드"란 구독으로 서비스를 받는 업체(브랜드)를 말합니다. 구독 화면에 표시됩니다.',
        '"목표 질문"이란 대상 브랜드가 AI 답변에 노출되기를 원하는 질문으로서 회사가 확정해 보고서에 표시한 것을 말합니다.',
        '"보고서"란 회사가 제공하는 성과 보고 화면(https://report.livegpt.co.kr)을 말합니다.',
        '"구독 개시일"이란 구독 화면에 표시된 첫 구독 기간의 시작일을 말합니다. 표시된 날이 없으면 첫 이용료가 결제된 날입니다.',
        '"구독일"이란 구독 개시일의 일(日)로서 매월 이용료가 결제되는 날을 말합니다.',
        '"구독 기간"이란 구독일부터 다음 구독일 전날까지를 말합니다.',
      ],
    },

    { kind: 'h2', text: '제3조 (구독의 시작)' },
    {
      kind: 'ol',
      items: [
        '이용자가 구독 화면에서 대상 브랜드·월 이용료·결제 카드를 확인하고 이 약관에 동의한 뒤 첫 이용료가 결제되면 구독이 시작됩니다. 이 동의로 별도의 서면 계약서 작성을 갈음합니다.',
        '이용자는 대상 브랜드를 대신해 구독을 시작하고 관리할 권한이 있음을 보증합니다.',
        '회사는 첫 결제가 확인된 날부터 목표 질문의 측정을 시작하고 보고서를 제공합니다.',
      ],
    },

    { kind: 'h2', text: '제4조 (구독 기간과 자동 갱신)' },
    {
      kind: 'ol',
      items: [
        '구독은 1개월 단위이며, 이용자가 제13조에 따라 해지를 신청하지 않으면 매 구독 기간이 끝나는 날의 다음 날에 같은 조건으로 자동 갱신되고 이용료가 자동결제됩니다.',
      ],
    },

    { kind: 'h2', text: '제5조 (서비스 내용)' },
    {
      kind: 'ol',
      items: [
        '회사는 아래 [서비스 명세]의 서비스를 구독 기간 동안 제공합니다.',
        '[서비스 명세]의 제공 수량은 기준 수량입니다. 회사는 AI 검색 엔진의 변화, 매체·플랫폼의 정책, 성과 분석 결과 등 합리적인 사유가 있으면 수량과 구성을 조정할 수 있으며, 조정한 결과는 보고서의 작업 내역으로 보여 드립니다. 이 조정은 이용료 감액이나 환불의 사유가 되지 않습니다.',
      ],
    },

    { kind: 'h2', text: '제6조 (목표 질문)' },
    {
      kind: 'ol',
      items: [
        '목표 질문은 회사가 이용자의 신청을 확인해 확정하고 보고서에 표시합니다. 이용료에는 확정된 목표 질문이 포함됩니다.',
        '이용자는 전자우편·카카오톡 등으로 목표 질문의 추가·변경을 신청할 수 있고, 회사가 확인 답장을 보내면 추가·변경된 것으로 봅니다.',
        '질문을 추가하면 이용료가 추가될 수 있으며, 추가 요금은 이용자와 회사가 협의하여 정합니다. 질문의 문구를 바꾸는 것만으로는 요금이 추가되지 않습니다.',
        '질문의 추가·변경과 추가 요금은 다음 구독 기간이 시작되는 날(다음 결제일)부터 적용합니다. 추가·변경한 질문의 측정은 그날부터 새로 시작합니다.',
      ],
    },

    { kind: 'h2', text: '제7조 (이용료와 결제)' },
    {
      kind: 'ol',
      items: [
        '이용료는 구독 화면에 표시된 금액(부가가치세 포함)이며 선불 카드 자동결제로 냅니다.',
        '첫 이용료는 이용자가 구독을 시작할 때 결제하며, 첫 구독 기간은 구독 개시일부터입니다. 구독 개시일이 지난 뒤에 시작해도 구독 기간과 구독일은 구독 개시일을 기준으로 하고, 구독 개시일부터 한 구독 기간 이상 지나 시작하면 지나간 구독 기간은 청구하지 않고 시작한 날이 속한 구독 기간부터 결제합니다.',
        '이후 매월 구독일에 등록된 카드로 그 구독 기간의 이용료를 자동결제합니다. 그 달에 구독일이 없으면 그 달의 마지막 날에 결제합니다.',
        '결제 증빙은 카드 매출전표로 갈음합니다.',
        '결제에 실패하면 회사는 자동으로 다시 결제하지 않습니다. 이용자는 구독 관리 화면에서 카드를 바꾸거나 다시 결제할 수 있습니다.',
        '카드 정보는 결제대행사(페이업)로 바로 전달되며, 회사는 카드번호 끝 4자리와 유효기간만 보관합니다.',
        '이용료를 바꾸려면 회사는 다음 구독 기간이 시작되기 30일 전까지 이용자에게 알립니다. 이용자가 바뀐 이용료에 동의하지 않으면 제13조에 따라 해지할 수 있습니다.',
      ],
    },

    { kind: 'h2', text: '제8조 (측정과 보고)' },
    {
      kind: 'p',
      text: '회사는 목표 질문을 매일 반복 측정하고 그 결과와 작업 내역(발행한 기사·블로그·영상 등)을 보고서로 제공하며, 이로써 별도의 통지를 갈음합니다. 보고서는 전날까지의 측정을 반영합니다.',
    },

    { kind: 'h2', text: '제9조 (서비스의 성격 — 노출 비보장)' },
    {
      kind: 'ol',
      items: [
        '이 서비스는 ChatGPT 등 AI 검색의 노출을 보장하는 서비스가 아니라, 인용을 위해 [서비스 명세]의 콘텐츠를 발행하고 관리하는 서비스입니다. 회사는 특정 질문에서의 노출·인용, 브랜드 언급률, 검색 순위, 문의 수나 매출을 보장하지 않습니다.',
        'AI 답변은 외부 사업자가 운영하는 서비스의 결과이며 같은 질문에도 답변이 매번 달라질 수 있습니다. 보고서 수치의 변동은 이용료 감액·환불의 사유가 되지 않습니다.',
      ],
    },

    { kind: 'h2', text: '제10조 (이용자의 협조)' },
    {
      kind: 'ol',
      items: [
        '이용자는 서비스에 필요한 자료(업체 정보, 진료·서비스 내용, 사진 등)와 필요한 접근 권한을 회사에 제공합니다.',
        '이용자가 제공한 자료가 사실과 다르거나 제3자의 권리를 침해해 생긴 문제는 이용자가 책임집니다.',
      ],
    },

    { kind: 'h2', text: '제11조 (콘텐츠의 권리와 구독 종료 후 삭제)' },
    {
      kind: 'ol',
      items: [
        '회사가 서비스로 만들고 발행한 홈페이지·기사·블로그·영상 등의 콘텐츠와 회사의 측정 시스템·작업 도구·보고서에 관한 권리는 회사에 있으며, 이용자는 구독 기간 동안 이를 이용할 수 있습니다.',
        '구독이 끝나면 회사는 서비스로 발행·게시한 콘텐츠를 끝난 날부터 7일 안에 삭제하거나 비공개로 바꾸며, 삭제한 콘텐츠는 복구하지 않습니다.',
        '구독이 끝나면 회사는 대상 브랜드의 콘텐츠가 있던 자리(순위·추천 사이트의 순위, 매체·채널의 게재 자리 등)에 다른 업체를 등록하거나 다른 업체의 콘텐츠를 발행할 수 있습니다.',
      ],
    },

    { kind: 'h2', text: '제12조 (비밀 유지와 개인정보)' },
    {
      kind: 'ol',
      items: [
        '회사와 이용자는 서비스와 관련해 알게 된 상대방의 영업상 비밀(이용료, 작업 방법, 측정 결과 등)을 구독 기간과 구독이 끝난 뒤 2년 동안 제3자에게 알리지 않습니다. 법령에 따라 공개해야 하는 경우는 예외입니다.',
        '회사는 서비스 제공에 필요한 범위에서만 이용자의 정보를 처리하며, 「개인정보 보호법」과 회사의 개인정보처리방침에 따라 보호합니다.',
      ],
    },

    { kind: 'h2', text: '제13조 (해지와 환불)' },
    {
      kind: 'ol',
      items: [
        `해지는 회사 담당자에게 전자우편(${COMPANY.email})·카카오톡 채널 등으로 신청합니다. 회사가 확인하면 다음 구독 기간의 자동결제가 취소되고, 이미 결제된 구독 기간이 끝나는 날에 구독이 끝납니다. 회사는 그날까지 서비스를 제공합니다.`,
        '이미 결제된 이용료는 환불하지 않습니다. 구독 기간 중간에 해지하더라도 남은 기간에 대한 일할 계산이나 환불은 없습니다.',
        '회사의 귀책사유로 어느 구독 기간에 서비스를 제공하지 못한 경우, 회사는 이후 구독 기간에 보충하거나 같은 수준의 다른 작업으로 대체해 제공하며 회사의 책임은 이에 한합니다.',
        '매체·플랫폼의 정책 변경·게재 거부·계정 제한, 이용자의 자료 제공이나 협조의 지연, 천재지변·시스템 장애 등 회사가 통제할 수 없는 사유로 서비스를 제공하지 못한 것은 회사의 귀책사유로 보지 않습니다.',
      ],
    },

    { kind: 'h2', text: '제14조 (손해배상)' },
    {
      kind: 'p',
      text: '회사 또는 이용자가 고의 또는 과실로 이 약관을 위반해 상대방에게 손해를 입히면 그 손해를 배상합니다. 다만 회사의 배상 책임은 고의 또는 중대한 과실이 없는 한 손해가 생기기 직전 3개월 동안 이용자가 결제한 이용료의 합계를 넘지 않습니다.',
    },

    { kind: 'h2', text: '제15조 (약관의 변경)' },
    {
      kind: 'p',
      text: '회사는 관련 법령을 위반하지 않는 범위에서 이 약관을 바꿀 수 있습니다. 바꾸는 경우 적용일과 바꾸는 이유를 적용일 7일 전(이용자에게 불리한 변경은 30일 전)부터 보고서 화면에 공지하고 전자우편으로 알립니다. 이용자가 바뀐 약관에 동의하지 않으면 해지할 수 있습니다.',
    },

    { kind: 'h2', text: '제16조 (분쟁 해결)' },
    {
      kind: 'p',
      text: '서비스에 관한 분쟁은 회사와 이용자가 성실히 협의해 해결하고, 협의가 되지 않으면 서울중앙지방법원을 제1심 관할 법원으로 합니다. 이 약관에 없는 사항은 관련 법령과 일반 상관례에 따릅니다.',
    },

    { kind: 'h2', text: '[서비스 명세]' },
    {
      kind: 'table',
      head: ['서비스', '내용', '제공 수량'],
      rows: SUBSCRIPTION_SERVICES.map((v) => [v.name, v.detail, v.quantity]),
    },

    { kind: 'h2', text: '부칙' },
    { kind: 'p', text: `이 약관은 ${SUBSCRIPTION_TERMS_VERSION}부터 시행합니다.` },
  ],
};

/**
 * 시작 화면의 요약(확인 사항) — 본문에서 대상·결제·해지에 관한 핵심만. 본문과 **같은 내용**이어야 한다(위 머리 주석)
 * 자리표시는 화면이 그 구독의 값으로 바꾼다: `{query}` 질문 · `{brand}` 브랜드명(둘은 굵게) · `{amount}` 월 이용료 ·
 * `{schedule}` 결제 일정(「구독 개시일 2026-10-20부터 매월 20일에」·「매월 같은 날」). `{query}` 줄은 질문이 없는 구독에서 빠진다
 */
export const SUBSCRIPTION_TERMS_SUMMARY: string[] = [
  '{query} 질문에 대한 AI 검색 최적화 서비스입니다.',
  '{brand} 브랜드가 AI 검색에 인용될 수 있게 콘텐츠를 발행하고 관리하는 서비스입니다. 노출·인용률을 보장하는 서비스가 아닙니다.',
  '구독을 시작할 때 첫 이용료 {amount}이 결제되고, 이후 {schedule} 같은 카드로 자동결제됩니다(1개월 단위 자동 갱신).',
  '해지는 담당자에게 요청해 주세요. 이미 결제된 기간이 끝나는 날 종료되며, 결제된 이용료는 환불되지 않습니다.',
  '구독이 끝나면 발행한 콘텐츠는 7일 안에 삭제됩니다.',
];
